import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatNumber, toMonthLabel } from "./financialUtils";

export default function CobranzaChartCard({
  barData = [],
  open,
  onToggle,
  onClose,
  draftFilters,
  setDraftFilters,
  centroOptions = [],
  cursoOptions = [],
  promoOptions = [],
  cobranzaYears = [],
  cobranzaMonths = [],
  onResetFilters,
  onApplyFilters,
}) {
  const totalCobrado = barData.reduce((acc, c) => acc + Number(c.cobrado || 0), 0);
  const cobranzasColor = "#0ea5e9";

  return (
    <div className="card admin-section-card h-100">
      <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
        <div>
          <h6 className="mb-0">Cobranza por Dia</h6>
          <small className="text-muted">Montos cobrados por fecha de pago</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="admin-filter-menu">
            <button
              type="button"
              className="btn btn-filter-chip"
              onClick={onToggle}
              aria-expanded={open}
            >
              <i className="bi bi-sliders2 me-1" />
              Filtros
              <i className="bi bi-chevron-down ms-1" />
            </button>
            {open && (
              <div className="admin-filter-panel">
                <div className="admin-filter-header">
                  <div>
                    <p className="admin-filter-title">Cobranza por dia</p>
                    <small className="text-muted">Filtra por mes y ano</small>
                  </div>
                  <button type="button" className="btn btn-light btn-sm" onClick={onClose}>
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Centro</label>
                    <select
                      className="form-select"
                      value={draftFilters.centro_id}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          centro_id: e.target.value,
                          curso_id: "",
                          promocion_id: "",
                        }))
                      }
                    >
                      <option value="">Todos</option>
                      {centroOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.alias || c.nombre || `Centro ${c.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Curso</label>
                    <select
                      className="form-select"
                      value={draftFilters.curso_id}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          curso_id: e.target.value,
                          promocion_id: "",
                        }))
                      }
                    >
                      <option value="">Todos</option>
                      {cursoOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre || `Curso ${c.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                        <div className="col-6">
                          <label className="form-label">Promocion</label>
                          <select
                            className="form-select"
                            value={draftFilters.promocion_id}
                            onChange={(e) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                promocion_id: e.target.value,
                              }))
                            }
                          >
                            <option value="">Todas</option>
                            {promoOptions.length ? (
                              promoOptions.map((p) => (
                                <option key={p.id} value={p.id}>
                                  {p.numero_promocion || p.nombre || `Promo ${p.id}`}
                                </option>
                              ))
                            ) : (
                              <option value="" disabled>
                                Sin promociones
                              </option>
                            )}
                          </select>
                        </div>
                  <div className="col-6">
                    <label className="form-label">Ano</label>
                    <select
                      className="form-select"
                      value={draftFilters.cobranzaYear}
                      onChange={(e) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          cobranzaYear: e.target.value,
                          cobranzaMonth: e.target.value === "all" ? "all" : prev.cobranzaMonth,
                        }))
                      }
                    >
                      <option value="all">Todos</option>
                      {cobranzaYears.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Mes</label>
                    <select
                      className="form-select"
                      value={draftFilters.cobranzaMonth}
                      disabled={draftFilters.cobranzaYear === "all"}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, cobranzaMonth: e.target.value }))}
                    >
                      <option value="all">Todos</option>
                      {cobranzaMonths.map((month) => (
                        <option key={month} value={month}>
                          {toMonthLabel(month)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="admin-filter-actions">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onResetFilters}>
                    Restablecer
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={onApplyFilters}>
                    Aplicar
                  </button>
                </div>
              </div>
            )}
          </div>
          <span className="dashboard-chip">
            <i className="bi bi-bar-chart-line me-1" />
            {barData.length}
          </span>
        </div>
      </div>
      <div className="card-body chart-body">
        <div className="d-flex justify-content-between align-items-center mb-2 small text-muted">
          {/* <span>Sin metas: solo mostramos lo cobrado</span> */}
          <span>Total periodo: {formatNumber(totalCobrado, true)}</span>
        </div>
        {barData.length ? (
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e5e5" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(v) => formatNumber(v)} />
                <Tooltip
                  formatter={(value) => [formatNumber(value, true), "Cobrado"]}
                  labelFormatter={(label, payload) => {
                    const row = payload && payload[0] ? payload[0].payload : null;
                    return row && row.fechaLabel ? `${row.fechaLabel}` : label;
                  }}
                />
                <Bar dataKey="cobrado" name="Cobrado" fill={cobranzasColor} radius={[6, 6, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="chart-empty text-muted">Sin datos de cobranza</div>
        )}
      </div>
    </div>
  );
}
